import { useState, useEffect } from 'react';
import SharedDialog from '../../../shared/shared-dialog';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import {
  isAbleToSeeUIElement,
  VaultRoles,
} from '../../../utils/roles/client-roles';
import { useUserSettings } from '../../../utils/use-user-settings';
import { useVaultActions } from './hooks/use-vault-actions';
import { VAULT_MATTER_REGION_OPTIONS } from '../../../../types';

import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';

interface CreateVaultMatterDialogProps {
  open: boolean;
  handleClose: () => void;
  setSelectedMatterId: (id: string) => void;
  isDecom?: boolean;
  initialCreateMatterOnDecom?: boolean;
  initialCustomText?: string;
  initialProfileField?: string;
  initialDescription?: string;
  initialMatterRegion?: string;
  onSaveDecomTemplate?: (values: {
    createMatterOnDecom: boolean;
    matterNameTemplateCustomText: string;
    matterNameTemplateProfileField: string;
    matterNameTemplateDescription: string;
    matterRegion: string;
  }) => void;
}

export default function CreateVaultMatterDialog({
  open,
  handleClose,
  setSelectedMatterId,
  isDecom,
  initialCreateMatterOnDecom,
  initialCustomText,
  initialProfileField,
  initialDescription,
  onSaveDecomTemplate,
  initialMatterRegion,
}: CreateVaultMatterDialogProps) {
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const { changeLogRoles, toolsRoles } = useUserSettings();

  const [name, setName] = useState('');
  const [description, setDescription] = useState(initialDescription || '');
  const [createMatterOnDecom, setCreateMatterOnDecom] = useState(
    initialCreateMatterOnDecom || false
  );
  const [customText, setCustomText] = useState(initialCustomText || '');
  const [profileField, setProfileField] = useState(() => {
    let initialField = initialProfileField || '{|Full Name|}';
    if (initialField === '{|Email|}') {
      initialField = '{|Primary Email|}';
    }
    return initialField;
  });
  const [matterRegion, setMatterRegion] = useState(
    initialMatterRegion || 'ANY'
  );

  useEffect(() => {
    if (open) {
      setCreateMatterOnDecom(initialCreateMatterOnDecom || false);
      setCustomText(initialCustomText || '');
      let initialField = initialProfileField || '{|Full Name|}';
      if (initialField === '{|Email|}') {
        initialField = '{|Primary Email|}';
      }
      setProfileField(initialField);
      setMatterRegion(initialMatterRegion || 'ANY');
      setDescription(initialDescription || '');
      setName('');
    }
  }, [
    open,
    initialCreateMatterOnDecom,
    initialCustomText,
    initialProfileField,
    initialDescription,
    initialMatterRegion,
  ]);

  const { createMatter, isCreatingMatter } = useVaultActions();

  const onSubmit = () => {
    if (isDecom && createMatterOnDecom) {
      if (onSaveDecomTemplate) {
        onSaveDecomTemplate({
          createMatterOnDecom: true,
          matterNameTemplateCustomText: customText,
          matterNameTemplateProfileField: profileField,
          matterNameTemplateDescription: description,
          matterRegion,
        });
      }
      handleCloseDialog();
      return;
    }

    const vaultCreatePermissions = [
      VaultRoles.CreateVaultExports?.value,
      VaultRoles.CreateGmailVaultExport?.value,
      VaultRoles.CreateChatVaultExport?.value,
      VaultRoles.CreateGeminiVaultExport?.value,
      VaultRoles.CreateCalendarVaultExport?.value,
      VaultRoles.CreateDriveVaultExport?.value,
      VaultRoles.CreateGroupsVaultExport?.value,
    ].filter((val): val is string => typeof val === 'string');

    const requiresChangeLog = vaultCreatePermissions.some(
      (permission) =>
        isAbleToSeeUIElement(toolsRoles, permission) &&
        isAbleToSeeUIElement(changeLogRoles, permission)
    );

    if (requiresChangeLog) {
      setOpenChangeLogDialog(true);
    } else {
      createMatter(
        { name, description, changeLogMessage },
        {
          onSuccess: (data) => {
            if (data && data.matterId) {
              setSelectedMatterId(data.matterId);
            }
            setOpenChangeLogDialog(false);
            setChangeLogMessage('');
            handleCloseDialog();
          },
        }
      );
    }
  };

  const handleCloseDialog = () => {
    setName('');
    setDescription('');
    setCreateMatterOnDecom(initialCreateMatterOnDecom || false);
    setCustomText(initialCustomText || '');
    setProfileField(initialProfileField || '{|Full Name|}');
    handleClose();
  };

  return (
    <>
      <SharedDialog
        open={open}
        handleClose={handleCloseDialog}
        title="Create Vault Matter"
        maxWidth="sm"
        dialogContent={
          <Stack gap={2} sx={{ mt: 1 }}>
            <TextField
              value={
                isDecom && createMatterOnDecom
                  ? `${customText || 'Departed Employee'}-${profileField}`
                  : name
              }
              onChange={(e) => setName(e.target.value)}
              label="Matter Name"
              fullWidth
              required
              disabled={isDecom && createMatterOnDecom}
              autoFocus={!(isDecom && createMatterOnDecom)}
            />

            {isDecom && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={createMatterOnDecom}
                    onChange={(e) => setCreateMatterOnDecom(e.target.checked)}
                  />
                }
                label={
                  <>
                    Create New Matter on User Decommission{' '}
                    <strong>every time</strong>
                  </>
                }
                sx={{ textAlign: 'center', width: 'fit-content' }}
              />
            )}

            {isDecom && createMatterOnDecom && (
              <>
                <Stack direction="row" gap={2} alignItems="flex-start">
                  <TextField
                    value={customText}
                    onChange={(e) => setCustomText(e.target.value)}
                    label="Custom Text"
                    sx={{ width: '50%' }}
                    required
                    helperText={`Preview: ${
                      customText || 'Departed Employee'
                    }-${
                      {
                        '{|Full Name|}': 'John Doe',
                        '{|Primary Email|}': 'johndoe@org.com',
                        '{|User ID|}': '123456789',
                      }[profileField] || ''
                    }`}
                  />
                  <TextField
                    select
                    value={profileField}
                    onChange={(e) => setProfileField(e.target.value)}
                    label="Profile Field"
                    sx={{ width: '50%' }}
                    required
                    helperText="Ex. {|Primary Email|}=user@org.com"
                  >
                    <MenuItem value="{|Full Name|}">{'{|Full Name|}'}</MenuItem>
                    <MenuItem value="{|Primary Email|}">
                      {'{|Primary Email|}'}
                    </MenuItem>
                    <MenuItem value="{|User ID|}">{'{|User ID|}'}</MenuItem>
                  </TextField>
                </Stack>
              </>
            )}
            <TextField
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              label="Description (optional)"
              fullWidth
            />
            <TextField
              select
              value={matterRegion}
              onChange={(e) => setMatterRegion(e.target.value)}
              label="Data Region"
              fullWidth
            >
              {VAULT_MATTER_REGION_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        }
        primaryButton={{
          title: isDecom && createMatterOnDecom ? 'Save' : 'Create Matter',
          action: onSubmit,
          disabled:
            isDecom && createMatterOnDecom
              ? !customText.trim() || !profileField.trim()
              : isCreatingMatter || !name.trim(),
          loading: isCreatingMatter,
        }}
      />
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          createMatter(
            { name, description, changeLogMessage },
            {
              onSuccess: (data) => {
                if (data && data.matterId) {
                  setSelectedMatterId(data.matterId);
                }
                setOpenChangeLogDialog(false);
                setChangeLogMessage('');
                handleCloseDialog();
              },
            }
          );
        }}
        mutateLoading={isCreatingMatter}
      />
    </>
  );
}
